import { ArrowDown, ArrowUp, Copy, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Loader } from "@/components/common/loader";
import { listNoticeDefinitions } from "@/features/notices/api";
import type { NoticeDefinitionOption, NoticeTemplate } from "@/features/notices/types";
import {
  copyTemplateSteps,
  deleteTemplateStep,
  getNoticeTemplate,
  reorderTemplateSteps,
  updateTemplateGuidance,
} from "@/features/notices/template-api";
import { PageHeader } from "@/components/common/page-header";
import { TabShell } from "@/features/settings/components/tab-shell";
import { TemplateStepDialog } from "@/features/settings/components/tabs/notice-template-step-dialog";
import { useFirmSettings } from "@/features/settings/hooks/use-firm-settings";
import { cn } from "@/lib/utils";

/**
 * Settings › Notice Templates — the firm's per-code default step checklist +
 * guidance overlay on the global notice-definition registry. Left: searchable
 * code list. Right: General (read-only header + editable guidance) and Steps
 * sub-tabs. Writes are admin-only (the backend also enforces it).
 */
export function NoticeTemplatesTab() {
  const { isAdmin } = useFirmSettings();
  const [definitions, setDefinitions] = useState<NoticeDefinitionOption[]>([]);
  const [defsLoading, setDefsLoading] = useState(true);
  const [defsError, setDefsError] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    listNoticeDefinitions()
      .then((list) => {
        if (!active) return;
        setDefinitions(list);
        setSelectedCode((cur) => cur ?? list[0]?.code ?? null);
      })
      .catch(() => active && setDefsError(true))
      .finally(() => active && setDefsLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return definitions;
    return definitions.filter(
      (d) => d.code.toLowerCase().includes(q) || d.title.toLowerCase().includes(q),
    );
  }, [definitions, search]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Notice Templates"
        subtitle="Default action-step checklists and guidance for each IRS notice code. New notices pre-populate from these."
      />
      <TabShell loading={defsLoading} error={defsError}>
        <div className="grid gap-6 md:grid-cols-[minmax(0,16rem)_1fr]">
        <div className="flex flex-col gap-2">
          <Input
            placeholder="Search notice codes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <ul className="border-border flex max-h-[28rem] flex-col gap-0.5 overflow-y-auto rounded-md border p-1">
            {filtered.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  onClick={() => setSelectedCode(d.code)}
                  className={cn(
                    "w-full rounded-md px-2 py-2 text-left text-sm",
                    selectedCode === d.code
                      ? "bg-primary/10 text-primary font-medium"
                      : "hover:bg-muted text-foreground",
                  )}
                >
                  <span className="font-semibold">{d.code}</span>
                  <span className="text-muted-foreground ml-1 text-xs">{d.title}</span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="text-muted-foreground px-2 py-2 text-sm">No matches.</li>
            )}
          </ul>
        </div>

        {selectedCode ? (
          <TemplateEditor
            key={selectedCode}
            code={selectedCode}
            codes={definitions}
            isAdmin={isAdmin}
          />
        ) : (
          <p className="text-muted-foreground text-sm">Select a notice code to edit.</p>
        )}
        </div>
      </TabShell>
    </div>
  );
}

function TemplateEditor({
  code,
  codes,
  isAdmin,
}: {
  code: string;
  codes: NoticeDefinitionOption[];
  isAdmin: boolean;
}) {
  const [template, setTemplate] = useState<NoticeTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [guidance, setGuidance] = useState("");
  const [saving, setSaving] = useState(false);

  const [stepDialog, setStepDialog] = useState<
    { step: NoticeTemplate["steps"][number] | null } | null
  >(null);
  const [deleting, setDeleting] = useState<NoticeTemplate["steps"][number] | null>(null);
  const [reordering, setReordering] = useState(false);
  const [copyFrom, setCopyFrom] = useState("");
  const [confirmCopy, setConfirmCopy] = useState(false);

  function load() {
    setLoading(true);
    getNoticeTemplate(code)
      .then((t) => {
        setTemplate(t);
        setGuidance(t.guidance);
      })
      .catch(() => toast.error("Couldn't load this template."))
      .finally(() => setLoading(false));
  }

  useEffect(load, [code]);

  async function saveGuidance() {
    setSaving(true);
    try {
      const updated = await updateTemplateGuidance(code, guidance);
      setTemplate(updated);
      toast.success("Guidance saved");
    } catch {
      toast.error("Couldn't save guidance.");
    } finally {
      setSaving(false);
    }
  }

  async function move(index: number, direction: -1 | 1) {
    if (!template) return;
    const steps = template.steps;
    const target = index + direction;
    if (target < 0 || target >= steps.length || reordering) return;
    const ids = steps.map((s) => s.id);
    const a = ids[index]!;
    ids[index] = ids[target]!;
    ids[target] = a;
    setReordering(true);
    try {
      await reorderTemplateSteps(code, ids);
      load();
    } catch {
      toast.error("Couldn't reorder steps.");
    } finally {
      setReordering(false);
    }
  }

  async function confirmDelete() {
    if (!deleting) return;
    try {
      await deleteTemplateStep(code, deleting.id);
      load();
    } catch {
      toast.error("Couldn't remove step.");
    } finally {
      setDeleting(null);
    }
  }

  async function runCopy() {
    setConfirmCopy(false);
    if (!copyFrom) return;
    try {
      const updated = await copyTemplateSteps(code, copyFrom);
      setTemplate(updated);
      setCopyFrom("");
      toast.success("Steps copied");
    } catch {
      toast.error("Couldn't copy steps.");
    }
  }

  if (loading || !template) {
    return (
      <div className="flex justify-center py-12">
        <Loader />
      </div>
    );
  }

  return (
    <Tabs defaultValue="general">
      <TabsList>
        <TabsTrigger value="general">General</TabsTrigger>
        <TabsTrigger value="steps">Steps</TabsTrigger>
      </TabsList>

      <TabsContent value="general" className="grid gap-4 pt-4">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-foreground text-lg font-semibold">
            {template.code} — {template.title}
          </h3>
          <Badge variant="outline">{template.severity}</Badge>
          <Badge variant="outline">{template.category}</Badge>
        </div>
        <p className="text-muted-foreground text-sm">{template.description}</p>
        <div className="grid gap-2">
          <Label htmlFor="guidance">Firm guidance</Label>
          <Textarea
            id="guidance"
            rows={5}
            value={guidance}
            disabled={!isAdmin}
            placeholder="Internal notes shown to staff working this notice code…"
            onChange={(e) => setGuidance(e.target.value)}
          />
        </div>
        {isAdmin ? (
          <div>
            <Button onClick={saveGuidance} disabled={saving || guidance === template.guidance}>
              Save guidance
            </Button>
          </div>
        ) : (
          <p className="text-muted-foreground text-xs">
            Only a country director can edit templates.
          </p>
        )}
      </TabsContent>

      <TabsContent value="steps" className="grid gap-4 pt-4">
        {isAdmin && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Select value={copyFrom} onValueChange={setCopyFrom}>
                <SelectTrigger className="w-56">
                  <SelectValue placeholder="Copy steps from…" />
                </SelectTrigger>
                <SelectContent>
                  {codes
                    .filter((c) => c.code !== code)
                    .map((c) => (
                      <SelectItem key={c.id} value={c.code}>
                        {c.code} — {c.title}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                disabled={!copyFrom}
                onClick={() => setConfirmCopy(true)}
              >
                <Copy className="size-4" /> Copy
              </Button>
            </div>
            <Button size="sm" onClick={() => setStepDialog({ step: null })}>
              <Plus className="size-4" /> Add Step
            </Button>
          </div>
        )}

        {template.steps.length === 0 ? (
          <p className="text-muted-foreground text-sm">No template steps yet.</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {template.steps.map((step, index) => (
              <li
                key={step.id}
                className="hover:bg-muted/40 flex items-center gap-3 rounded-md px-2 py-2"
              >
                <span className="text-muted-foreground w-5 text-right text-xs">
                  {index + 1}.
                </span>
                <span className="text-foreground flex-1 text-sm">{step.description}</span>
                {isAdmin && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Move "${step.description}" up`}
                      disabled={index === 0 || reordering}
                      onClick={() => void move(index, -1)}
                    >
                      <ArrowUp className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Move "${step.description}" down`}
                      disabled={index === template.steps.length - 1 || reordering}
                      onClick={() => void move(index, 1)}
                    >
                      <ArrowDown className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Edit "${step.description}"`}
                      onClick={() => setStepDialog({ step })}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Delete "${step.description}"`}
                      onClick={() => setDeleting(step)}
                    >
                      <Trash2 className="text-destructive size-4" />
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </TabsContent>

      {stepDialog && (
        <TemplateStepDialog
          key={stepDialog.step?.id ?? "new"}
          code={code}
          step={stepDialog.step}
          open
          onOpenChange={(open) => !open && setStepDialog(null)}
          onSaved={load}
        />
      )}

      <AlertDialog open={deleting !== null} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove template step?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes "{deleting?.description}" from the {code} template.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmCopy} onOpenChange={setConfirmCopy}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace {code}'s steps?</AlertDialogTitle>
            <AlertDialogDescription>
              This replaces all of {code}'s template steps with a copy of {copyFrom}'s
              steps. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runCopy}>Replace</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Tabs>
  );
}
